import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState, type ReactNode } from 'react'

import { formatPesewas } from '../../lib/money'
import {
  paymentMethodWords,
  whenLabel,
} from '../../pages/transport/bookingWords'
import { SERVICE_NAME } from '../../pages/transport/services'
import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  DESK_NOTE_MAX,
  REFUNDS_KEY,
  TRANSPORT_DESK_KEY,
  actOnBooking,
  addDeskNote,
  deskBookingKey,
  deskStatusLabel,
  fetchDeskBooking,
  sendDriverDetails,
  type DeskAction,
  type DeskBooking,
} from '../api/transportBookings'
import { DetailDrawer } from '../components/DetailDrawer'
import { StatusPill } from '../components/StatusPill'
import {
  answerWords,
  deskErrorMessage,
  mobilityWords,
  nextStatusButton,
  readRefundEntry,
  refundReasonWords,
  stamp,
} from '../transportDesk'

/**
 * One transport booking, for the desk (M40-T12, spec §F.2).
 *
 * Every control here is offered only when the booking's `allowed_actions`
 * holds it; the server checks again and refuses with a message, which shows
 * at the top of the drawer. Each action sends back the `updated_at` the desk
 * read, untouched: if someone else changed the booking first, the drawer
 * reloads it and says so, rather than acting on what is no longer there.
 *
 * "Hospital informed" means the desk told the hospital to expect the passenger,
 * never that the hospital accepted or confirmed anything (spec §C.3.6).
 */

/** The server's limits (`schemas/transport_desk.py`). */
const NAME_MAX = 100
const PLATE_MAX = 20
const PHONE_MAX = 20
const REASON_MAX = 300

const INPUT =
  'w-full rounded-[8px] border border-line-2 bg-bg-app px-2 py-1.5 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const BUTTON =
  'inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3.5 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50'
const PRIMARY =
  'inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3.5 text-sm font-semibold text-accent-on hover:opacity-90 disabled:opacity-50'
const DANGER =
  'inline-flex h-9 items-center justify-center rounded-[8px] bg-bed-bad px-3.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
const LABEL = 'flex flex-col gap-1 text-xs font-medium text-ink-2'
const FIELDSET = 'flex flex-col gap-2 rounded-[10px] border border-line-2 p-3'
const LEGEND = 'px-1 text-xs font-semibold text-ink-2'

/** Run one action; `then` runs only once the server has accepted it. */
type Act = (action: DeskAction, then?: () => void) => void

export function TransportBookingDrawer({
  bookingId,
  onClose,
}: {
  bookingId: string | null
  onClose: () => void
}): ReactNode {
  const { token } = useAuth()
  const detail = useQuery({
    queryKey: deskBookingKey(bookingId ?? ''),
    queryFn: () => fetchDeskBooking(token, bookingId ?? ''),
    enabled: Boolean(token) && bookingId !== null,
  })

  return (
    <DetailDrawer
      open={bookingId !== null}
      onClose={onClose}
      title={detail.data ? `Booking ${detail.data.reference}` : 'Booking'}
    >
      {/* Decision 2: always on the detail, where the phone call happens. */}
      <p
        role="note"
        className="mb-4 rounded-[10px] border border-bed-bad bg-bed-bad-bg px-3 py-2 text-sm text-bed-bad"
      >
        <span className="font-semibold">Passenger sounds unwell?</span> Tell
        them to call 112 now. Do not turn the trip into an emergency response.
      </p>
      {detail.isPending ? (
        <div
          role="status"
          aria-label="Loading the booking"
          className="h-40 rounded-xl bg-bg-sunken"
        />
      ) : detail.isError || !detail.data ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {detail.error instanceof ApiError
            ? detail.error.message
            : 'Could not load this booking.'}
        </p>
      ) : (
        // Keyed by the booking, not its version: a reload after someone
        // else's change keeps the message saying so.
        <BookingBody key={detail.data.id} data={detail.data} />
      )}
    </DetailDrawer>
  )
}

function BookingBody({ data }: { data: DeskBooking }): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  // Where focus goes after an action: the button pressed has often gone (an
  // action done is no longer offered), and a refusal shows up here.
  const topRef = useRef<HTMLDivElement>(null)
  const toTop = () => topRef.current?.focus()

  // In the mutations' own options, not per call: per-call callbacks are
  // skipped once the drawer has closed, and the queue must still refresh.
  const done = (fresh: DeskBooking) => {
    toTop()
    queryClient.setQueryData(deskBookingKey(data.id), fresh)
    // The queue and the dashboard's count; the refunds list, as an ending
    // action may have recorded one.
    void queryClient.invalidateQueries({
      queryKey: TRANSPORT_DESK_KEY,
      predicate: (q) => q.queryKey[2] !== 'booking',
    })
    void queryClient.invalidateQueries({ queryKey: REFUNDS_KEY })
  }
  const failed = (e: unknown) => {
    toTop()
    if (e instanceof ApiError && e.code === 'concurrent_modification') {
      setError(
        'Someone else changed this booking. It has been reloaded: check it and try again.',
      )
      void queryClient.invalidateQueries({ queryKey: deskBookingKey(data.id) })
      return
    }
    setError(deskErrorMessage(e))
  }

  const mutation = useMutation({
    mutationFn: (action: DeskAction) =>
      actOnBooking(token, data.id, data.updated_at, action),
    onSuccess: done,
    onError: failed,
  })
  const act: Act = (action, then) => {
    setError(null)
    mutation.mutate(action, { onSuccess: () => then?.() })
  }
  const send = useMutation({
    mutationFn: () => sendDriverDetails(token, data.id),
    onMutate: () => setError(null),
    onSuccess: done,
    onError: failed,
  })
  const busy = mutation.isPending || send.isPending
  const can = (a: DeskBooking['allowed_actions'][number]) =>
    data.allowed_actions.includes(a)

  return (
    <div className="flex flex-col gap-6 text-sm text-ink-1">
      <div
        ref={topRef}
        tabIndex={-1}
        data-testid="desk-top"
        className="flex flex-col gap-3 focus:outline-none"
      >
        {error ? (
          <p
            role="alert"
            className="whitespace-pre-line rounded-[8px] border border-bed-bad/40 bg-bed-bad-bg px-3 py-2 font-medium text-bed-bad"
          >
            {error}
          </p>
        ) : null}
        <Summary data={data} />
      </div>
      <Trip data={data} />
      <People data={data} />

      <section aria-labelledby="desk-driver" className="flex flex-col gap-3">
        <h3 id="desk-driver" className="font-semibold">
          Driver
        </h3>
        <DriverDetails data={data} />
        {can('send_driver_details') && data.sms_live ? (
          <button
            type="button"
            className={PRIMARY}
            disabled={busy}
            onClick={() => send.mutate()}
          >
            Send driver details
          </button>
        ) : null}
        {can('details_given') ? (
          <div className="flex flex-col gap-1">
            {data.sms_live ? null : (
              <p className="text-ink-2">
                Text messages are not live yet. Phone the booker with the
                driver’s details, then record it.
              </p>
            )}
            <button
              type="button"
              className={BUTTON}
              disabled={busy}
              onClick={() => act({ action: 'details_given' })}
            >
              Details given by phone
            </button>
          </div>
        ) : null}
        {can('assign') ? (
          <AssignForm data={data} act={act} busy={busy} />
        ) : null}
      </section>

      {data.return_mode !== null || data.return_driver_name !== null ? (
        <section aria-labelledby="desk-return" className="flex flex-col gap-3">
          <h3 id="desk-return" className="font-semibold">
            Return driver
          </h3>
          {data.return_driver_name ? (
            <p>
              {data.return_driver_name} ·{' '}
              <Phone number={data.return_driver_phone} /> ·{' '}
              {data.return_vehicle_plate}
            </p>
          ) : (
            <p className="text-ink-3">Not assigned yet.</p>
          )}
          {can('assign_return') ? (
            <AssignReturnForm data={data} act={act} busy={busy} />
          ) : null}
        </section>
      ) : null}

      <section aria-labelledby="desk-hospital" className="flex flex-col gap-2">
        <h3 id="desk-hospital" className="font-semibold">
          Hospital
        </h3>
        {data.hospital_informed_at ? (
          <p>
            Hospital informed (told to expect them) ·{' '}
            {stamp(data.hospital_informed_at)}
          </p>
        ) : (
          <p className="text-ink-2">
            Phone {data.hospital}’s main desk with the passenger’s first name
            and arrival time. This tells them to expect the passenger; it is not
            their acceptance.
          </p>
        )}
        {can('hospital_informed') ? (
          <button
            type="button"
            className={BUTTON}
            disabled={busy}
            onClick={() => act({ action: 'hospital_informed' })}
          >
            Hospital informed (told to expect them)
          </button>
        ) : null}
      </section>

      {can('status') && data.next_status ? (
        <section aria-labelledby="desk-day" className="flex flex-col gap-2">
          <h3 id="desk-day" className="font-semibold">
            Day of travel
          </h3>
          <button
            type="button"
            className={PRIMARY}
            disabled={busy}
            onClick={() =>
              act({ action: 'status', status: data.next_status ?? '' })
            }
          >
            {nextStatusButton(data.next_status)}
          </button>
        </section>
      ) : null}

      <Endings data={data} act={act} busy={busy} />

      {data.allowed_actions.length === 0 ? (
        <p className="text-ink-3">Nothing to do on this booking now.</p>
      ) : null}

      <RefundsDue data={data} />
      <Notes data={data} />
    </div>
  )
}

function Phone({ number }: { number: string | null }): ReactNode {
  if (!number) return '—'
  return (
    <a
      href={`tel:${number}`}
      className="font-semibold text-accent-ink underline"
    >
      {number}
    </a>
  )
}

function Rows({ rows }: { rows: Array<[string, ReactNode]> }): ReactNode {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-ink-3">{label}</dt>
          <dd className="min-w-0 whitespace-pre-wrap break-words">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function Summary({ data }: { data: DeskBooking }): ReactNode {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <StatusPill status={data.status} label={deskStatusLabel(data.status)} />
      {data.past_cutoff ? (
        <span className="rounded-full bg-bed-bad px-2 py-0.5 text-xs font-semibold text-white">
          Past cut-off
        </span>
      ) : null}
      {data.over_capacity ? (
        <span className="rounded-full bg-bed-warn-bg px-2 py-0.5 text-xs font-semibold text-ink-1">
          Over capacity
        </span>
      ) : null}
      {data.return_requested_at ? (
        <span className="rounded-full bg-bed-warn-bg px-2 py-0.5 text-xs font-semibold text-ink-1">
          Return requested {stamp(data.return_requested_at)}
        </span>
      ) : null}
    </div>
  )
}

function Trip({ data }: { data: DeskBooking }): ReactNode {
  const map =
    data.pickup_lat !== null && data.pickup_lng !== null
      ? `https://www.google.com/maps?q=${data.pickup_lat},${data.pickup_lng}`
      : null
  const rows: Array<[string, ReactNode]> = [
    [
      'Service',
      SERVICE_NAME[data.service as keyof typeof SERVICE_NAME] ?? data.service,
    ],
    ['Pickup', whenLabel(data.pickup_at)],
  ]
  if (data.appointment_at)
    rows.push(['Appointment', whenLabel(data.appointment_at)])
  rows.push([
    'From',
    <>
      {data.pickup_address}, {data.pickup_area}
      {data.pickup_landmark ? ` (${data.pickup_landmark})` : ''}
      {map ? (
        <>
          {' · '}
          <a
            href={map}
            target="_blank"
            rel="noreferrer"
            className="text-accent-ink underline"
          >
            Open the map
          </a>
        </>
      ) : null}
    </>,
  ])
  rows.push(['To', `${data.hospital} (zone ${data.zone})`])
  if (data.journey) rows.push(['Journey', answerWords(data.journey)])
  if (data.return_mode)
    rows.push([
      'Return',
      data.return_pickup_at
        ? `${answerWords(data.return_mode)}, ${whenLabel(data.return_pickup_at)}`
        : answerWords(data.return_mode),
    ])
  if (data.hire_length)
    rows.push(['Hire length', answerWords(data.hire_length)])
  if (data.hire_stops) rows.push(['Stops', data.hire_stops])
  rows.push(['Total paid', formatPesewas(data.total_pesewas)])
  if (data.payment_method)
    rows.push(['Paid by', paymentMethodWords(data.payment_method)])
  if (data.confirmed_at) rows.push(['Paid on', stamp(data.confirmed_at)])
  if (data.cancel_reason) rows.push(['Reason', data.cancel_reason])
  rows.push([
    'Private link',
    data.link_active ? 'Active' : 'Revoked, or never made',
  ])
  return (
    <section aria-labelledby="desk-trip" className="flex flex-col gap-2">
      <h3 id="desk-trip" className="font-semibold">
        The trip
      </h3>
      <Rows rows={rows} />
    </section>
  )
}

function People({ data }: { data: DeskBooking }): ReactNode {
  const rows: Array<[string, ReactNode]> = [
    ['Booker', `${data.booker_first_name} ${data.booker_last_name}`],
    ['Booker’s phone', <Phone key="b" number={data.booker_phone} />],
    [
      'Passenger',
      data.passenger_is_booker
        ? 'The booker'
        : [
            data.passenger_name,
            data.passenger_age !== null ? `aged ${data.passenger_age}` : null,
          ]
            .filter(Boolean)
            .join(', '),
    ],
  ]
  if (!data.passenger_is_booker && data.passenger_phone)
    rows.push([
      'Passenger’s phone',
      <Phone key="p" number={data.passenger_phone} />,
    ])
  rows.push([
    'Mobility',
    `${mobilityWords(data.mobility)}${data.uses_oxygen ? ', uses oxygen' : ''}`,
  ])
  if (data.equipment.length > 0)
    rows.push(['Equipment', data.equipment.map(answerWords).join(', ')])
  rows.push(['Companion', answerWords(data.companion)])
  if (data.driver_notes) rows.push(['For the driver', data.driver_notes])
  return (
    <section aria-labelledby="desk-people" className="flex flex-col gap-2">
      <h3 id="desk-people" className="font-semibold">
        The people
      </h3>
      <Rows rows={rows} />
    </section>
  )
}

function DriverDetails({ data }: { data: DeskBooking }): ReactNode {
  if (!data.driver_name) return <p className="text-ink-3">Not assigned yet.</p>
  return (
    <div className="flex flex-col gap-1">
      <p>
        {data.driver_name} · <Phone number={data.driver_phone} /> ·{' '}
        {data.vehicle_plate}
        {data.attendant_name ? ` · attendant ${data.attendant_name}` : ''}
      </p>
      <p className="text-ink-2">
        {data.driver_details_given_at
          ? `Details given ${stamp(data.driver_details_given_at)}`
          : 'Details not yet given to the customer.'}
      </p>
    </div>
  )
}

function AssignForm({
  data,
  act,
  busy,
}: {
  data: DeskBooking
  act: Act
  busy: boolean
}): ReactNode {
  const changing = data.driver_name !== null
  const [open, setOpen] = useState(!changing)
  const [name, setName] = useState(data.driver_name ?? '')
  const [phone, setPhone] = useState(data.driver_phone ?? '')
  const [plate, setPlate] = useState(data.vehicle_plate ?? '')
  const [attendant, setAttendant] = useState(data.attendant_name ?? '')
  const pts = data.service === 'pts'
  const needsAttendant = pts && attendant.trim() === ''
  const attendantHint = useId()
  const ready =
    name.trim() !== '' &&
    phone.trim() !== '' &&
    plate.trim() !== '' &&
    !needsAttendant

  if (!open) {
    return (
      <button type="button" className={BUTTON} onClick={() => setOpen(true)}>
        Change the driver…
      </button>
    )
  }
  const legend = changing ? 'Change the driver' : 'Assign a driver'
  return (
    <fieldset className={FIELDSET}>
      <legend className={LEGEND}>{legend}</legend>
      <label className={LABEL}>
        Driver’s name
        <input
          className={INPUT}
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Driver’s phone
        <input
          className={INPUT}
          type="tel"
          value={phone}
          maxLength={PHONE_MAX}
          onChange={(e) => setPhone(e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Vehicle plate
        <input
          className={INPUT}
          value={plate}
          maxLength={PLATE_MAX}
          onChange={(e) => setPlate(e.target.value)}
        />
      </label>
      {pts ? (
        <div className="flex flex-col gap-1">
          <label className={LABEL}>
            Attendant’s name
            <input
              className={INPUT}
              value={attendant}
              maxLength={NAME_MAX}
              aria-describedby={attendantHint}
              onChange={(e) => setAttendant(e.target.value)}
            />
          </label>
          <p id={attendantHint} className="text-xs text-ink-3">
            Patient Transport needs the attendant’s name.
          </p>
        </div>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          className={PRIMARY}
          disabled={!ready || busy}
          onClick={() =>
            act(
              {
                action: 'assign',
                driver_name: name.trim(),
                driver_phone: phone.trim(),
                vehicle_plate: plate.trim(),
                ...(pts ? { attendant_name: attendant.trim() } : {}),
              },
              () => setOpen(false),
            )
          }
        >
          Assign
        </button>
        {changing ? (
          <button
            type="button"
            className={BUTTON}
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        ) : null}
      </div>
    </fieldset>
  )
}

function AssignReturnForm({
  data,
  act,
  busy,
}: {
  data: DeskBooking
  act: Act
  busy: boolean
}): ReactNode {
  const changing = data.return_driver_name !== null
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(data.return_driver_name ?? '')
  const [phone, setPhone] = useState(data.return_driver_phone ?? '')
  const [plate, setPlate] = useState(data.return_vehicle_plate ?? '')
  const ready = name.trim() !== '' && phone.trim() !== '' && plate.trim() !== ''

  if (!open) {
    return (
      <button type="button" className={BUTTON} onClick={() => setOpen(true)}>
        {changing ? 'Change the return driver…' : 'Assign the return driver…'}
      </button>
    )
  }
  return (
    <fieldset className={FIELDSET}>
      <legend className={LEGEND}>
        {changing ? 'Change the return driver' : 'Assign the return driver'}
      </legend>
      <label className={LABEL}>
        Return driver’s name
        <input
          className={INPUT}
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Return driver’s phone
        <input
          className={INPUT}
          type="tel"
          value={phone}
          maxLength={PHONE_MAX}
          onChange={(e) => setPhone(e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Return vehicle plate
        <input
          className={INPUT}
          value={plate}
          maxLength={PLATE_MAX}
          onChange={(e) => setPlate(e.target.value)}
        />
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          className={PRIMARY}
          disabled={!ready || busy}
          onClick={() =>
            act(
              {
                action: 'assign_return',
                return_driver_name: name.trim(),
                return_driver_phone: phone.trim(),
                return_vehicle_plate: plate.trim(),
              },
              () => setOpen(false),
            )
          }
        >
          Assign the return
        </button>
        <button type="button" className={BUTTON} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </fieldset>
  )
}

type Ending = 'no_show' | 'cannot_fulfil' | 'cancel' | 'revoke_link'

/** The actions that end a booking, or its link. Each asks to be confirmed,
 *  one open at a time. */
function Endings({
  data,
  act,
  busy,
}: {
  data: DeskBooking
  act: Act
  busy: boolean
}): ReactNode {
  const [chosen, setChosen] = useState<Ending | null>(null)
  const [reason, setReason] = useState('')
  const [amount, setAmount] = useState('')
  const amountHint = useId()
  const offered = (
    [
      ['no_show', 'No show…'],
      ['cannot_fulfil', 'Cannot fulfil…'],
      ['cancel', 'Customer cancelled…'],
      ['revoke_link', 'Revoke the private link…'],
    ] as const
  ).filter(([a]) => data.allowed_actions.includes(a))
  if (offered.length === 0) return null
  // A form stays open only while its action is still allowed: a reload after
  // someone else's change may have taken it away.
  const open = offered.some(([a]) => a === chosen) ? chosen : null

  const close = () => {
    setChosen(null)
    setReason('')
    setAmount('')
  }
  const total = formatPesewas(data.total_pesewas)
  const why = reason.trim()
  const refund = readRefundEntry(amount, data.total_pesewas)

  const reasonField = (
    <label className={LABEL}>
      Reason
      <input
        className={INPUT}
        value={reason}
        maxLength={REASON_MAX}
        onChange={(e) => setReason(e.target.value)}
      />
    </label>
  )
  const keep = (
    <button type="button" className={BUTTON} onClick={close}>
      Keep the booking
    </button>
  )

  return (
    <section aria-labelledby="desk-endings" className="flex flex-col gap-2">
      <h3 id="desk-endings" className="font-semibold">
        Problems
      </h3>
      {open === null ? (
        <div className="flex flex-wrap gap-2">
          {offered.map(([a, label]) => (
            <button
              key={a}
              type="button"
              className={BUTTON}
              disabled={busy}
              onClick={() => setChosen(a)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {open === 'cannot_fulfil' ? (
        <fieldset className={FIELDSET}>
          <legend className={LEGEND}>Cannot fulfil</legend>
          <p>
            Medcor cannot provide this trip. A full refund of {total} will be
            recorded as due, automatically. Phone the customer to tell them.
          </p>
          {reasonField}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={DANGER}
              disabled={why === '' || busy}
              onClick={() =>
                act({ action: 'cannot_fulfil', reason: why }, close)
              }
            >
              Yes, cannot fulfil
            </button>
            {keep}
          </div>
        </fieldset>
      ) : null}

      {open === 'cancel' ? (
        <fieldset className={FIELDSET}>
          <legend className={LEGEND}>Customer cancelled</legend>
          <p>
            Record the refund due under the cancellation policy, from 0 up to
            the {total} paid. No money moves here.
          </p>
          {reasonField}
          <label className={LABEL}>
            Refund due (GHS)
            <input
              className={INPUT}
              inputMode="decimal"
              value={amount}
              aria-invalid={!refund.ok && refund.problem !== null}
              aria-describedby={amountHint}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <p
            id={amountHint}
            className={
              !refund.ok && refund.problem
                ? 'text-xs font-medium text-bed-bad'
                : 'text-xs text-ink-3'
            }
          >
            {!refund.ok && refund.problem
              ? refund.problem
              : `From 0 up to ${total}, in cedis.`}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={DANGER}
              disabled={why === '' || !refund.ok || busy}
              onClick={() => {
                if (!refund.ok) return
                act(
                  {
                    action: 'cancel',
                    reason: why,
                    refund_pesewas: refund.pesewas,
                  },
                  close,
                )
              }}
            >
              Yes, cancel the booking
            </button>
            {keep}
          </div>
        </fieldset>
      ) : null}

      {open === 'no_show' ? (
        <fieldset className={FIELDSET}>
          <legend className={LEGEND}>No show</legend>
          <p>The driver could not find the passenger, so the trip ends here.</p>
          {reasonField}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={DANGER}
              disabled={why === '' || busy}
              onClick={() => act({ action: 'no_show', reason: why }, close)}
            >
              Yes, mark no show
            </button>
            {keep}
          </div>
        </fieldset>
      ) : null}

      {open === 'revoke_link' ? (
        <fieldset className={FIELDSET}>
          <legend className={LEGEND}>Revoke the private link</legend>
          <p>
            The customer’s link to this booking stops working at once. They can
            still phone the desk.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={DANGER}
              disabled={busy}
              onClick={() => act({ action: 'revoke_link' }, close)}
            >
              Yes, revoke the link
            </button>
            <button type="button" className={BUTTON} onClick={close}>
              Keep the link
            </button>
          </div>
        </fieldset>
      ) : null}
    </section>
  )
}

function RefundsDue({ data }: { data: DeskBooking }): ReactNode {
  if (data.refunds_due.length === 0) return null
  return (
    <section aria-labelledby="desk-refunds" className="flex flex-col gap-2">
      <h3 id="desk-refunds" className="font-semibold">
        Refunds due
      </h3>
      <ul className="flex flex-col gap-1">
        {data.refunds_due.map((r) => (
          <li key={r.id}>
            {formatPesewas(r.amount_pesewas)} · {refundReasonWords(r.reason)} ·{' '}
            {r.issued_at
              ? `Issued ${stamp(r.issued_at)}, ${r.issued_reference ?? ''}`
              : 'Not issued yet'}
          </li>
        ))}
      </ul>
    </section>
  )
}

function Notes({ data }: { data: DeskBooking }): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const add = useMutation({
    mutationFn: () => addDeskNote(token, data.id, text.trim()),
    onSuccess: () => {
      setText('')
      void queryClient.invalidateQueries({ queryKey: deskBookingKey(data.id) })
    },
  })
  return (
    <section aria-labelledby="desk-notes" className="flex flex-col gap-2">
      <h3 id="desk-notes" className="font-semibold">
        Notes
      </h3>
      {data.notes.length === 0 ? (
        <p className="text-ink-3">No notes yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.notes.map((n) => (
            <li key={n.id} className="rounded-[8px] bg-bg-soft px-3 py-2">
              <p className="whitespace-pre-wrap">{n.body}</p>
              <p className="text-xs text-ink-3">
                {n.author_name ?? 'Medcor (automatic)'} · {stamp(n.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
      <label className={LABEL}>
        Add a note
        <textarea
          className={INPUT}
          rows={2}
          value={text}
          maxLength={DESK_NOTE_MAX}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <p className="text-xs text-ink-3">
        Staff only: never shown to the customer.
      </p>
      <button
        type="button"
        className={BUTTON}
        disabled={text.trim() === '' || add.isPending}
        onClick={() => add.mutate()}
      >
        Add note
      </button>
      {add.isError ? (
        <p role="alert" className="font-medium text-bed-bad">
          {add.error instanceof ApiError
            ? add.error.message
            : 'Could not add the note.'}
        </p>
      ) : null}
    </section>
  )
}
